import type { TableState } from './tableViews';

/**
 * The data half of a column: what a cell MEANS, as opposed to what it looks like.
 *
 * Kept separate from the React column so filtering, sorting and export can be reasoned about
 * — and tested — without rendering anything. A column with no `value` is decoration and takes
 * no part in any of the three.
 */
export interface DataColumn {
  key: string;
  header: string;
  value?: (row: never) => string | number | null;
  filter?: 'text' | 'choice' | 'none';
}

type Valued<T> = {
  key: string;
  header: string;
  value?: (row: T) => string | number | null;
  /**
   * What to ORDER by, when that is not what the cell says.
   *
   * Priority is the case that forces it: the column reads "Critical", "High", "Medium", and
   * sorting those as words puts Critical between a comment and a heading. The rank sorts, the
   * word filters and exports.
   */
  sortValue?: (row: T) => string | number | null;
  filter?: 'text' | 'choice' | 'none';
};

/** Empty for the purposes of ordering: no value, or nothing in it. */
function isEmpty(value: string | number | null | undefined): boolean {
  return value === null || value === undefined || value === '';
}

/**
 * The rows a set of filters leaves, in the order the sort asks for.
 *
 * `visibleKeys` is what the free-text search looks through — what is ON SCREEN. Searching a
 * column somebody has hidden would return rows with no visible reason for matching, which
 * reads as the search being broken.
 */
export function applyTableState<T>(
  rows: T[],
  columns: Valued<T>[],
  state: TableState,
  visibleKeys: string[],
): T[] {
  const query = state.search.trim().toLowerCase();
  const byKey = new Map(columns.map((column) => [column.key, column]));

  const matched = rows.filter((row) => {
    for (const [key, wanted] of Object.entries(state.filters)) {
      if (!wanted) continue;
      const column = byKey.get(key);
      if (!column?.value) continue;

      const value = column.value(row);
      const text = isEmpty(value) ? '' : String(value);

      /* A chosen value is exact; typed text is a substring, because people type fragments. */
      const hit = column.filter === 'choice' ? text === wanted : text.toLowerCase().includes(wanted.toLowerCase());
      if (!hit) return false;
    }

    if (!query) return true;

    return visibleKeys.some((key) => {
      const value = byKey.get(key)?.value?.(row);
      return !isEmpty(value) && String(value).toLowerCase().includes(query);
    });
  });

  if (!state.sort) return matched;

  const column = byKey.get(state.sort.key);
  const order = column?.sortValue ?? column?.value;
  if (!order) return matched;

  const direction = state.sort.direction === 'asc' ? 1 : -1;

  return [...matched].sort((left, right) => {
    const a = order(left);
    const b = order(right);

    /**
     * Empty always sinks, in BOTH directions.
     *
     * A task with no due date is not the most urgent thing on the list, and reversing the sort
     * must not make it so. An unknown is not a value; sorting it as one is how a list ends up
     * asserting something nobody said.
     */
    if (isEmpty(a) && isEmpty(b)) return 0;
    if (isEmpty(a)) return 1;
    if (isEmpty(b)) return -1;

    if (typeof a === 'number' && typeof b === 'number') return (a - b) * direction;
    return String(a).localeCompare(String(b), undefined, { numeric: true }) * direction;
  });
}

/** The values actually present in a column, for a filter that offers no dead ends. */
export function choicesFor<T>(rows: T[], column: Valued<T>): string[] {
  if (!column.value) return [];

  const seen = new Set<string>();
  for (const row of rows) {
    const value = column.value(row);
    if (!isEmpty(value)) seen.add(String(value));
  }

  return [...seen].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
}

/**
 * One CSV field.
 *
 * The leading apostrophe is not decoration: a cell beginning `=`, `+`, `-` or `@` is a FORMULA
 * to Excel, and a title somebody typed is not something to hand a spreadsheet as code.
 */
export function csvField(value: string | number | null | undefined): string {
  const text = isEmpty(value) ? '' : String(value);
  const guarded = /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
  return `"${guarded.replace(/"/g, '""')}"`;
}

/** CRLF because that is what Excel expects of a csv; the caller adds the BOM. */
export function toCsv<T>(columns: Valued<T>[], rows: T[]): string {
  const usable = columns.filter((column) => column.value);
  const lines = [usable.map((column) => csvField(column.header)).join(',')];

  for (const row of rows) lines.push(usable.map((column) => csvField(column.value!(row))).join(','));
  return lines.join('\r\n');
}
